import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Mail, Phone, MapPin, Send, Clock, 
  MessageSquare, DollarSign, Thermometer
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inquiryTypes = [
  'Product Demo',
  'Pricing Information',
  'Technical Support',
  'Partnership Inquiry',
  'General Question',
  'Other'
];

const contactInfo = [
  {
    icon: Mail,
    label: 'Email',
    value: 'contact@trackotic.com',
    href: 'mailto:contact@trackotic.com'
  },
  {
    icon: Phone,
    label: 'Phone',
    value: '+91 79825 85277',
    href: 'tel:+917982585277'
  },
  {
    icon: MapPin,
    label: 'Office',
    value: 'Villa No 97, Basement 3, Block 3, Charmwood Village, Faridabad',
    href: '#'
  },
  {
    icon: Clock,
    label: 'Hours',
    value: 'Mon-Fri: 9AM - 6PM IST',
    href: '#'
  }
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    inquiry_type: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, inquiry_type: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.inquiry_type || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        service_interest: formData.inquiry_type
      });
      toast.success('Message sent successfully! We\'ll be in touch soon.');
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        inquiry_type: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Contact form error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-16 bg-white">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <Badge className="mb-6 bg-blue-100 text-blue-700 border-blue-200">
              CONTACT US
            </Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Get in Touch with
              <span className="text-primary"> Track<span className="text-red-500">0</span>tic</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Have questions about our cold chain monitoring platform? Interested in pricing? 
              Fill out the form below and our team will respond within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Info Banner */}
      <section className="py-6 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Looking for Pricing Information?</h3>
                <p className="text-blue-100 text-sm">
                  Select "Pricing Information" in the form below and we'll provide a customized quote.
                </p>
              </div>
            </div>
            <Badge className="bg-white/20 text-white border-white/30">
              Custom Quotes Available
            </Badge>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3 bg-white rounded-2xl p-8 shadow-lg"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Thermometer className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-semibold text-gray-900">
                    Send Us a Message
                  </h2>
                  <p className="text-sm text-gray-500">We'll respond within 24 hours</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-gray-700">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      className="h-12 border-gray-300 focus:border-primary"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-700">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@company.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="h-12 border-gray-300 focus:border-primary"
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-gray-700">Company / Organization</Label>
                    <Input
                      id="company"
                      name="company"
                      placeholder="Your Company"
                      value={formData.company}
                      onChange={handleChange}
                      className="h-12 border-gray-300 focus:border-primary"
                      data-testid="contact-company-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-gray-700">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      placeholder="+91 98765 43210"
                      value={formData.phone}
                      onChange={handleChange}
                      className="h-12 border-gray-300 focus:border-primary"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2 relative" style={{ zIndex: 100 }}>
                  <Label className="text-gray-700">What can we help you with? *</Label>
                  <Select value={formData.inquiry_type} onValueChange={handleSelectChange}>
                    <SelectTrigger 
                      className="h-12 border-gray-300"
                      data-testid="contact-inquiry-select"
                    >
                      <SelectValue placeholder="Select inquiry type" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999] bg-white">
                      {inquiryTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type === 'Pricing Information' ? (
                            <span className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4 text-green-600" />
                              {type}
                            </span>
                          ) : type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-gray-700">Your Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder={formData.inquiry_type === 'Pricing Information' 
                      ? "Please tell us about your organization, number of vehicles/devices needed, and any specific requirements..."
                      : "Tell us about your requirements, questions, or how we can help..."
                    }
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className="border-gray-300 focus:border-primary resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full md:w-auto bg-primary hover:bg-blue-700 text-white font-semibold h-12 px-8 rounded-lg"
                  data-testid="contact-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                  <Send className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-2 space-y-6"
            >
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="font-heading text-lg font-semibold text-gray-900 mb-6">
                  Contact Information
                </h3>
                <div className="space-y-6">
                  {contactInfo.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      className="flex items-start gap-4 group"
                    >
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          {item.label}
                        </div>
                        <div className="text-gray-900 group-hover:text-primary transition-colors">
                          {item.value}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">
                  Quick Response
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Our team typically responds within 24 hours during business days. 
                  For urgent inquiries, please call our hotline.
                </p>
                <div className="flex items-center gap-2 text-primary">
                  <MessageSquare className="w-4 h-4" />
                  <span className="text-sm font-medium">Average response: 4 hours</span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                <DollarSign className="w-8 h-8 text-primary mb-4" />
                <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">
                  Pricing Inquiries
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  We offer customized pricing based on your fleet size and requirements. 
                  Contact us for a tailored quote for your organization.
                </p>
                <div className="text-sm text-gray-500">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                    <span>Flexible subscription plans</span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                    <span>Volume discounts available</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                    <span>Free demo & consultation</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
